import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAddManagerToGroup, useRemoveManagerFromGroup } from '@/hooks/groups.hook';
import { confirmToast, successToast, errorToast } from '@/common/toast';
import { DataGrid } from '@/components/common';
import { useUserList } from '@/hooks/users.hook';
import { UserTypeCell } from '../../components';

/**
 * 매니저 추가 모달 컴포넌트
 * 
 * 전체 매니저 목록을 조회하고 그룹에 추가할 수 있는 모달입니다.
 * 
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {number} props.groupId - 그룹 ID
 * @param {Set<string>} props.groupManagerIds - 이미 그룹에 소속된 매니저 ID Set
 * @param {Function} props.onSuccess - 매니저 추가 성공 시 콜백
 */
const AddManagerModal = ({
    open,
    onClose,
    groupId,
    groupManagerIds,
    onSuccess,
}) => {
    // 검색 상태
    const [searchType, setSearchType] = useState('all');
    const [searchKeyword, setSearchKeyword] = useState('');

    // 실제 쿼리용 상태
    const [queryOptions, setQueryOptions] = useState({
        type: 'all',
        search: ''
    });

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    // 매니저 추가 mutation
    const addManagerMutation = useAddManagerToGroup();
    // 매니저 제거 mutation
    const removeManagerMutation = useRemoveManagerFromGroup();

    // 전체 사용자 목록 조회
    const { data: allManagersData, isLoading: isAllManagersLoading } = useUserList({
        page: paginationModel.page + 1,
        size: paginationModel.pageSize,
        isStudent: false,
        searchType: queryOptions.type,
        keyword: queryOptions.search,
    });

    // 전체 사용자 목록
    const allManagersRows = allManagersData?.list ?? [];
    const allManagersTotal = allManagersData?.total ?? 0;

    const [rowCountState, setRowCountState] = useState(0);
    useEffect(() => {
        if (!isAllManagersLoading) setRowCountState(allManagersTotal);
    }, [allManagersTotal, isAllManagersLoading]);

    // 검색 실행
    const handleSearch = useCallback(() => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        setQueryOptions({
            type: searchType,
            search: searchKeyword,
        });
    }, [searchType, searchKeyword]);

    // 검색 초기화
    const handleReset = useCallback(() => {
        setSearchType('all');
        setSearchKeyword('');
        setQueryOptions({ type: 'all', search: '' });
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, []);

    

    // 매니저 추가 핸들러
    const handleAddManager = useCallback((e, params) => {
        e.stopPropagation();
        const name = `${params.row.name}(${params.row.userId})`;
        confirmToast(`${name} 매니저를 이 그룹에 추가하시겠습니까?`, async () => {
            try {
                await addManagerMutation.mutateAsync({
                    groupId,
                    userId: params.row.userId,
                });
                successToast('매니저가 추가되었습니다.');
            } catch (error) {
                errorToast(`매니저 추가에 실패했습니다. - ${error.response?.data?.msg}`);
            }
            onSuccess?.();
        });
    }, [onSuccess]);

    // 매니저 제거 핸들러
    const handleRemoveManager = useCallback((e, params) => {
        e.stopPropagation();
        const name = `${params.row.name}(${params.row.userId})`;
        confirmToast(`${name} 매니저를 이 그룹에서 제거하시겠습니까?`, async () => {
            try {
                await removeManagerMutation.mutateAsync({
                    groupId,
                    userId: params.row.userId,
                });
                successToast('매니저가 제거되었습니다.');
            } catch (error) {
                errorToast(`매니저 제거에 실패했습니다. - ${error.response?.data?.msg}`);
            }
            onSuccess?.();
        });
    }, [onSuccess]);

    // 전체 매니저 컬럼 정의
    const columns = useMemo(() => [
        { field: 'userId', headerName: '아이디', minWidth: 120, flex: 1 },
        {
            field: 'name',
            headerName: '이름 (이메일)',
            minWidth: 200,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box display="flex" gap={1}>
                    <span>{params.row.name}</span>
                    <span style={{ fontSize: '12px', color: '#888' }}>({params.row.email})</span>
                </Box>
            )
        },
        {
            field: 'type',
            headerName: '권한',
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => <UserTypeCell row={params.row} />
        },
        {
            field: 'actions',
            headerName: '작업',
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => {
                const isAdmin = params.row.userRole === 'ADMIN'
                const isDisabled = isAdmin;
                const isAlreadyAdded = groupManagerIds.includes(params.row.userId);
                // admin은 버튼 비활성화
                // 이미 추가된 매니저는 제거 버튼
                // 아닌 경우는 추가 버튼
                return (
                    <>
                        {!isDisabled && (
                                <Button
                                    variant={isAlreadyAdded ? 'outlined' : 'contained'}
                                    color={isAlreadyAdded ? 'error' : 'primary'}
                                    size="small"
                                    startIcon={isAlreadyAdded ? <RemoveIcon /> : <AddIcon />}
                                    disabled={isDisabled}
                                    onClick={(e) => {
                                        if (isAlreadyAdded) {
                                            handleRemoveManager(e, params);
                                        } else {
                                            handleAddManager(e, params);
                                        }
                                    }}
                                >
                                    {isAlreadyAdded ? '제거' : '추가'}
                                </Button>
                            )
                        }
                    </>
                );
            },
        }
    ], [groupManagerIds, handleAddManager, handleRemoveManager]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                매니저 추가
            </DialogTitle>
            <DialogContent>
                {/* 검색 필터 */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2} sx={{ mt: 2 }}>
                    <Stack direction="row" gap={1} alignItems="center">
                        <FormControl size='small' sx={{ width: 120 }}>
                            <InputLabel>검색 조건</InputLabel>
                            <Select
                                value={searchType}
                                label="검색 조건"
                                onChange={(e) => setSearchType(e.target.value)}
                            >
                                <MenuItem value='all'>전체</MenuItem>
                                <MenuItem value='userId'>아이디</MenuItem>
                                <MenuItem value='name'>이름</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            size='small'
                            placeholder='검색어 입력'
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
                            sx={{ width: 200 }}
                        />

                        <Button variant='contained' onClick={handleSearch} sx={{ height: 40 }}>검색</Button>
                        <Button variant='outlined' onClick={handleReset} sx={{ height: 40 }}>초기화</Button>
                    </Stack>
                </Stack>

                {/* 전체 매니저 데이터 그리드 */}
                <DataGrid
                    rows={allManagersRows}
                    columns={columns}
                    loading={isAllManagersLoading}
                    rowCount={rowCountState}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowId={(row) => row.userId}
                    height={500}
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'default',
                        },
                        '& .MuiDataGrid-row:hover': {
                            cursor: 'default',
                        },
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>닫기</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddManagerModal;
